import { useMemo } from 'react';
import { FormsSubmission } from '@frontend/api-forms';
import { routes as formRoutes, routeUtils as formRouteUtils, pendoTags as formsPendoTags } from '@frontend/forms';
import { useTranslation } from '@frontend/i18n';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useFormsNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const formsSubmissionCount = FormsSubmission.useSubmissionsUnreadCount();

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          count: formsSubmissionCount.data?.count,
          label: t('Submissions'),
          relativePath: '/submissions',
          path: `/forms${formRouteUtils.getRoutePathAsString(
            formRoutes.submissionsV2.goTo({ listType: 'inbox', subCategory: 'all', relativePath: true })
          )}`,
          trackingId: formsPendoTags.sideNav.submissions,
          description:
            'Displays user-submitted form data. Ideal for reviewing patient intake, consent forms, or survey responses.',
        },
        {
          label: t('My Forms'),
          relativePath: '/library',
          path: `/forms${formRouteUtils.getRoutePathAsString(formRoutes.library.formsV2({ relativePath: true }))}`,
          trackingId: formsPendoTags.sideNav.myForms,
          description:
            'Collection of all created forms in your account. Manage, duplicate, or edit your practice’s custom digital forms.',
        },
        {
          label: t('Kiosk Mode'),
          relativePath: '/kiosk',
          trackingId: formsPendoTags.sideNav.kiosk,
          description: 'A simplified, patient-facing mode for in-office form completion on a tablet or kiosk.',
        },
        {
          label: t('Templates'),
          relativePath: '/templates',
          path: `/forms${formRouteUtils.getRoutePathAsString(
            formRoutes.templates.templatesList({ relativePath: true })
          )}`,
          trackingId: formsPendoTags.sideNav.templates,
          description: 'Library of form templates. Browse, select, and adapt pre-built templates for quick deployment.',
        },
        {
          label: t('Builder'),
          relativePath: '/builder/new',
          trackingId: formsPendoTags.sideNav.builder,
          description: 'Create or customize digital forms from scratch using a drag-and-drop interface.',
        },
        {
          label: t('Request Digitization'),
          relativePath: '/digitization',
          trackingId: formsPendoTags.sideNav.requestDigitization,
          description: 'Request professional conversion of paper or PDF forms into interactive digital versions.',
        },
      ]),
    [t, formsSubmissionCount.data?.count]
  );

  return useMemo(
    () =>
      createNavItem('forms', {
        hasCount: !!formsSubmissionCount.data?.count,
        iconName: 'forms',
        label: t('Forms'),
        root: '/forms',
        subNavItems,
        settings: { path: '/digital-forms', label: '', show: true },
      }),
    [t, formsSubmissionCount.data?.count, subNavItems]
  );
};
